import React from 'react'
import { Icon, type IDropdownItem, type IIconsVector } from 'shared/ui'
import type { IAgentHandoffNotifySettings } from 'entities/Chatbot'

export type ITemplateKey =
  | 'addTag'
  | 'removeTag'
  | 'sendSMS'
  | 'addContactNote'
  | 'addConversationNote'
  | 'handoffToHuman'
  | 'stopConversation'

type ITemplateInfo = {
  key: ITemplateKey
  label: string
  icon: IIconsVector
}

type ITemplateData<T> = {
  key: ITemplateKey
  label: string
  icon: IIconsVector
  templateInfo: ITemplateInfo
  createAction: (data?: T) => IAgentAction
  dropdownItem: IDropdownItem
  shortRegex: string
  regex: string
}

export type ITokenTemplate = { token_id: string }
export type ITagActionItem = {
  name: string
  id: number
}
export type ITagActionData = { tags: ITagActionItem[] } | ITokenTemplate

export type IMessageActionData = { content: string } | { auto: true } | ITokenTemplate

const makeTemplate = <T,>(
  templateData: Pick<ITemplateData<T>, 'key' | 'label' | 'icon'>
): ITemplateData<T> => {
  const { key, label, icon } = templateData
  const shortRegex = `@${key}\\((.+)?\\)`
  return {
    ...templateData,
    templateInfo: { key, label, icon },
    createAction: (data) => ({
      key,
      label,
      template: `@${templateData.key}(${data ? JSON.stringify(data) : ''})`,
    }),
    dropdownItem: {
      id: key,
      label: label,
      customIconL: <Icon icon={icon} color={'var(--content-brand-primary)'} />,
    },
    shortRegex,
    regex: `^${shortRegex}$`,
  }
}

export type IHandoffToHumanChipData = IAgentHandoffNotifySettings | ITokenTemplate
export const addTagTemplate = makeTemplate<ITagActionData>({
  key: 'addTag',
  label: 'Add tag',
  icon: 'tag',
})

export const removeTagTemplate = makeTemplate<ITagActionData>({
  key: 'removeTag',
  label: 'Remove tag',
  icon: 'tagRemove',
})

export const sendSMSTemplate = makeTemplate<IMessageActionData>({
  key: 'sendSMS',
  label: 'Send SMS',
  icon: 'messageTextCircle02',
})
export const addContactNoteTemplate = makeTemplate<IMessageActionData>({
  key: 'addContactNote',
  label: 'Add contact note',
  icon: 'edit',
})
export const addConversationNoteTemplate = makeTemplate<IMessageActionData>({
  key: 'addConversationNote',
  label: 'Add conversation note',
  icon: 'edit',
})
export const handoffToHumanTemplate = makeTemplate<IHandoffToHumanChipData>({
  key: 'handoffToHuman',
  label: 'Handoff to human',
  icon: 'userRight',
})
export const stopConversationTemplate = makeTemplate({
  key: 'stopConversation',
  label: 'Stop agent',
  icon: 'stop',
})

const TemplateData: Record<ITemplateKey, ITemplateData<never>> = {
  addTag: addTagTemplate,
  removeTag: removeTagTemplate,
  sendSMS: sendSMSTemplate,
  addContactNote: addContactNoteTemplate,
  addConversationNote: addConversationNoteTemplate,
  handoffToHuman: handoffToHumanTemplate,
  stopConversation: stopConversationTemplate,
}

export type IAgentAction = {
  key: ITemplateKey
  label: string
  template: string
}

export const createActionChip = (action: IAgentAction): string => {
  const rootSpan = document.createElement('span')
  const wrapSpan = document.createElement('span')

  wrapSpan.className = 'actionChip-wrap'
  wrapSpan.setAttribute('data-readonly', '')
  wrapSpan.setAttribute('data-template', action.template)
  wrapSpan.setAttribute('contenteditable', 'false')

  const contentSpan = document.createElement('span')
  contentSpan.className = `actionChip-content actionChip-${action.key}`
  contentSpan.setAttribute('data-readonly', '')
  contentSpan.setAttribute('contenteditable', 'false')

  const iconSpan = document.createElement('span')
  iconSpan.className = `actionChip-icon actionChip-icon-${action.key}`
  iconSpan.setAttribute('contenteditable', 'false')

  const labelSpan = document.createElement('span')
  labelSpan.className = 'actionChip-label'
  labelSpan.setAttribute('contenteditable', 'false')
  labelSpan.innerText = action.label

  contentSpan.appendChild(iconSpan)
  contentSpan.appendChild(labelSpan)
  wrapSpan.appendChild(contentSpan)
  rootSpan.appendChild(wrapSpan)
  return rootSpan.innerHTML
}

const decodeHtmlEntities = (str: string): string =>
  str
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')

export const actionChipReplacer = (template: string) => {
  const decoded = decodeHtmlEntities(template)

  for (const { createAction, regex } of Object.values(TemplateData)) {
    const match = decoded.match(regex)
    if (match) {
      try {
        return createActionChip(createAction(match[1] ? JSON.parse(match[1]) : undefined))
      } catch {
        return template
      }
    }
  }
  return template
}
